import _extends from "@babel/runtime/helpers/esm/extends";
import React, { useRef, forwardRef, useState, useEffect } from "react";
import ClassNames from "classnames";
import { Checkbox } from "../checkbox";
import { useControlled, KEY_VALUES, mergeRefs } from "../utils";
var Table = /*#__PURE__*/forwardRef(function (props, ref) {
  var id = props.id,
    originalData = props.originalData,
    _props$condensed = props.condensed,
    condensed = _props$condensed === void 0 ? false : _props$condensed,
    striped = props.striped,
    tableId = props.tableId,
    _props$animatedSortin = props.animatedSorting,
    animatedSorting = _props$animatedSortin === void 0 ? true : _props$animatedSortin,
    children = props.children,
    _props$checkable = props.checkable,
    checkable = _props$checkable === void 0 ? false : _props$checkable,
    _props$hasTitle = props.hasTitle,
    hasTitle = _props$hasTitle === void 0 ? false : _props$hasTitle,
    sortActive = props.sortActive,
    sortPhase = props.sortPhase,
    skipInitialSort = props.skipInitialSort,
    disableDefaultSort = props.disableDefaultSort,
    style = props.style,
    className = props.className,
    currentPage = props.currentPage,
    _props$pageSize = props.pageSize,
    pageSize = _props$pageSize === void 0 ? 50 : _props$pageSize,
    onCheckChange = props.onCheckChange,
    onSort = props.onSort,
    _props$selectedIndexs = props.selectedIndexs,
    selectedIndexs = _props$selectedIndexs === void 0 ? [] : _props$selectedIndexs,
    onRowClick = props.onRowClick,
    _props$customSelected = props.customSelectedItemKey,
    customSelectedItemKey = _props$customSelected === void 0 ? "id" : _props$customSelected;
  var tableRef = useRef(null);
  var originalInfo = currentPage ? originalData.slice((currentPage - 1) * pageSize, currentPage * pageSize) : originalData;
  var noNav = !currentPage && currentPage !== 0;
  var _useState = useState([].concat(originalInfo)),
    sortedData = _useState[0],
    setSortedData = _useState[1];
  var initialData = useRef([].concat(originalData));
  var _useControlled = useControlled(sortActive, ''),
    sortAct = _useControlled[0],
    setSortAct = _useControlled[1];
  var _useControlled2 = useControlled(sortPhase, skipInitialSort ? 1 : 0),
    sortPha = _useControlled2[0],
    setSortPha = _useControlled2[1];
  var _useState2 = useState(tableId),
    idInfo = _useState2[0],
    setIdInfo = _useState2[1];
  var _useState3 = useState(false),
    tableHeadPadding = _useState3[0],
    setTableHeadPadding = _useState3[1];
  var setStrips = function setStrips() {
    var _tableRef$current;
    var tbody = (_tableRef$current = tableRef.current) === null || _tableRef$current === void 0 ? void 0 : _tableRef$current.querySelector('tbody');
    var trs = tbody === null || tbody === void 0 ? void 0 : tbody.getElementsByTagName('tr');
    trs && Array.from(trs).forEach(function (element, i) {
      if (i % 2 === 0) {
        element.classList.add('striped');
      }
    });
  };
  var context = React.useMemo(function () {
    return {
      originalData: originalData,
      sortAct: sortAct,
      sortPha: sortPha
    };
  }, [originalData, sortAct, sortPha]);
  var initStrips = function initStrips() {
    setTimeout(function () {
      setStrips();
    });
  };
  var initSortedData = function initSortedData() {
    var initData = [].concat(originalData);
    initData.forEach(function (data, i) {
      data.selected = false;
      Object.assign(data, {
        _initialSortIndex: i,
        _previousSortIndex: i,
        _nextSortIndex: i
      });
    });
    initialData.current = [].concat(initData);
    if (currentPage) {
      setSortedData([].concat(initData.slice((currentPage - 1) * pageSize, currentPage * pageSize)));
    } else {
      setSortedData([].concat(initData));
    }
  };
  useEffect(function () {
    if (currentPage) {
      setSortedData([].concat(initialData.current.slice((currentPage - 1) * pageSize, currentPage * pageSize)));
    }
  }, [currentPage, pageSize]);
  useEffect(function () {
    if (!disableDefaultSort) {
      // Initialize sortedData with four new variables
      initSortedData();
    }
  }, [originalData]);
  useEffect(function () {
    // Initialize table strips
    initStrips();
    if (!disableDefaultSort) {
      // Remove sorted style for each column
      sortedData.forEach(function (item, index) {
        var _tableRef$current2, _tableRef$current3;
        var hoverHead = (_tableRef$current2 = tableRef.current) === null || _tableRef$current2 === void 0 ? void 0 : _tableRef$current2.querySelectorAll('.header-container')[index];
        var sortIcon = (_tableRef$current3 = tableRef.current) === null || _tableRef$current3 === void 0 ? void 0 : _tableRef$current3.querySelectorAll('.icon-sorting-outline')[index];
        if (sortIcon) {
          sortIcon.classList.remove('sorted');
        }
        if (hoverHead) {
          hoverHead.classList.remove('head-sorted');
        }
      });
    } else {
      setSortedData([].concat(originalInfo));
    }
  }, [context]);
  useEffect(function () {
    if (!idInfo) setIdInfo(String.fromCharCode(65 + Math.floor(Math.random() * 26)) + Date.now());
  }, [tableId]);
  useEffect(function () {
    setStrips();
  }, [selectedIndexs]);
  useEffect(function () {
    var _tableRef$current4, _tableRef$current5, _tableRef$current6, _tableRef$current7;
    if (!((_tableRef$current4 = tableRef.current) !== null && _tableRef$current4 !== void 0 && _tableRef$current4.querySelectorAll('th') && (_tableRef$current5 = tableRef.current) !== null && _tableRef$current5 !== void 0 && _tableRef$current5.querySelectorAll('th') && ((_tableRef$current6 = tableRef.current) === null || _tableRef$current6 === void 0 ? void 0 : _tableRef$current6.querySelectorAll('th').length) > 0 && (_tableRef$current7 = tableRef.current) !== null && _tableRef$current7 !== void 0 && _tableRef$current7.querySelectorAll('th')[0].querySelector('.ap-checkbox-container'))) setTableHeadPadding(true);
  }, []);
  useEffect(function () {
    if (Array.isArray(children) && children.length > 0) {
      sort(sortAct, sortPha);
    }
  }, [context]);
  var handleSort = function handleSort(key) {
    if (key !== sortAct) {
      setSortAct(key);
      setSortPha(1);
      onSort === null || onSort === void 0 ? void 0 : onSort(key, 1);
    } else {
      var step = sortPha < 2 ? skipInitialSort ? 2 : sortPha + 1 : skipInitialSort ? 1 : 0;
      setSortPha(step);
      onSort === null || onSort === void 0 ? void 0 : onSort(sortAct, step);
    }
  };
  var animate = function animate(element, animation) {
    element.classList.add(animation);
    setTimeout(function () {
      element.classList.remove(animation);
    }, 300);
  };
  var setDescription = function setDescription(sort, sortKey, index, order) {
    var _tableRef$current8;
    var descriptionNode = (_tableRef$current8 = tableRef.current) === null || _tableRef$current8 === void 0 ? void 0 : _tableRef$current8.querySelectorAll('.ap-table-header-description-sr-only')[index];
    if (descriptionNode) {
      descriptionNode.innerHTML = sort ? "Click to sort rows by " + sortKey + " in " + order + " order" : "Click to unsort rows by " + sortKey;
    }
  };
  var removeIconClass = function removeIconClass() {
    var _tableRef$current9;
    var headers = (_tableRef$current9 = tableRef.current) === null || _tableRef$current9 === void 0 ? void 0 : _tableRef$current9.querySelectorAll('th');
    headers && Array.from(headers).forEach(function (element) {
      var hoverHead = element.querySelector('.header-container');
      var defaultIcon = hoverHead === null || hoverHead === void 0 ? void 0 : hoverHead.querySelector('.icon-sorting-fill');
      var sortupIcon = hoverHead === null || hoverHead === void 0 ? void 0 : hoverHead.querySelector('.icon-sorting-ascendant-fill');
      if (defaultIcon) {
        defaultIcon.classList.remove('sorted');
        defaultIcon.classList.remove('hidden');
      }
      if (sortupIcon) {
        sortupIcon.classList.remove('sorted', 'descendant');
        sortupIcon.classList.add('hidden');
      }
      hoverHead === null || hoverHead === void 0 ? void 0 : hoverHead.classList.remove('head-sorted');
      element.removeAttribute('aria-sort');
    });
  };
  var defaultSortFunc = function defaultSortFunc(itemA, itemB, sortKey, sortType) {
    var a = itemA[sortKey],
      b = itemB[sortKey];
    if (!(a || a === 0)) {
      return 1;
    }
    if (!(b || b === 0)) {
      return -1;
    }
    if (a === b) {
      return 0;
    }
    if (sortType === 2) {
      return a < b ? 1 : -1;
    } else {
      return a > b ? 1 : -1;
    }
  };
  var sortAnimate = function sortAnimate() {
    var _tableRef$current10, _initialData$current, _initialData$current2;
    var tbody = (_tableRef$current10 = tableRef.current) === null || _tableRef$current10 === void 0 ? void 0 : _tableRef$current10.querySelector('tbody');
    var trows = tbody === null || tbody === void 0 ? void 0 : tbody.getElementsByTagName('tr');
    if (noNav) initialData === null || initialData === void 0 ? void 0 : (_initialData$current = initialData.current) === null || _initialData$current === void 0 ? void 0 : _initialData$current.forEach(function (row, i) {
      if (trows && trows[i]) {
        trows[i].style.transition = 'background-color .3s, transform .3s';
      }
    });
    var trowsHeight = [];
    trows && Array.from(trows).forEach(function (row, i) {
      trowsHeight[i] = row.getBoundingClientRect().height;
    });

    // Initialize trowsSortedHeight everytime data is sorted
    var trowsSortedHeight = [];
    initialData === null || initialData === void 0 ? void 0 : (_initialData$current2 = initialData.current) === null || _initialData$current2 === void 0 ? void 0 : _initialData$current2.forEach(function (data, i) {
      trowsSortedHeight[i] = trowsHeight[data._previousSortIndex];
    });

    // Initialize trowsSortedHeightSum everytime data is sorted
    var trowsSortedHeightSum = [];
    trowsSortedHeight.reduce(function (a, b, i) {
      trowsSortedHeightSum[i] = a + b;
      return a + b;
    }, 0);
    if (noNav) {
      initialData.current.sort(function (a, b) {
        return a._previousSortIndex > b._previousSortIndex ? 1 : -1;
      });
      setSortedData([].concat(initialData.current));
    }
    setTimeout(function () {
      var _tableRef$current11;
      var tbody = (_tableRef$current11 = tableRef.current) === null || _tableRef$current11 === void 0 ? void 0 : _tableRef$current11.querySelector('tbody');
      var trows = tbody && Array.from(tbody.getElementsByTagName('tr'));
      if (!trows) return;
      trows.forEach(function (th) {
        th.classList.remove('striped');
      });
      var trowsHeightSum = [];
      trowsHeight.reduce(function (a, b, i) {
        trowsHeightSum[i] = a + b;
        return a + b;
      }, 0);
      if (noNav) animatedSorting && initialData.current.forEach(function (rowData, i) {
        var val = trowsSortedHeightSum[rowData._nextSortIndex] - trowsHeightSum[rowData._previousSortIndex] + 4 * (rowData._nextSortIndex - rowData._previousSortIndex);
        trows && (trows[i].style.transform = "translateY(" + val + "px)");
      });
      setTimeout(function () {
        if (noNav) initialData.current.forEach(function (row, i) {
          if (trows) {
            trows[i].style.transform = "none";
            trows[i].style.transition = "none";
          }
        });
        initialData.current.sort(function (a, b) {
          return a._nextSortIndex > b._nextSortIndex ? 1 : -1;
        });
        if (noNav) setSortedData([].concat(initialData.current));else setSortedData([].concat(initialData.current.slice((currentPage - 1) * pageSize, currentPage * pageSize)));
        setTimeout(function () {
          initStrips();
          setStrips();
        });
      }, 300); // wait for the transition time of trows
    }, 0);
  };
  var changeSortIcon = function changeSortIcon(sortKey, sortType, index, sortFunc1, sortFunc2, skipInitialSort) {
    var _tableRef$current12;
    var hoverHead = (_tableRef$current12 = tableRef.current) === null || _tableRef$current12 === void 0 ? void 0 : _tableRef$current12.querySelectorAll('.header-container')[index];
    // const sortIcon = hoverHead?.querySelector('.icon-sorting-outline') as HTMLSpanElement;
    var defaultIcon = hoverHead === null || hoverHead === void 0 ? void 0 : hoverHead.querySelector('.icon-sorting-fill');
    var sortupIcon = hoverHead === null || hoverHead === void 0 ? void 0 : hoverHead.querySelector('.icon-sorting-ascendant-fill');
    var order1 = sortFunc1 ? 'custom' : 'ascending';
    var order2 = sortFunc2 ? 'custom' : 'descending';
    if (sortType === 0 && !skipInitialSort) {
      var _tableRef$current13;
      animate(sortupIcon, 'rotateIn');
      setTimeout(function () {
        defaultIcon.classList.remove('hidden');
        sortupIcon.classList.add('hidden');
        hoverHead === null || hoverHead === void 0 ? void 0 : hoverHead.classList.remove('head-sorted');
      }, 300);
      (_tableRef$current13 = tableRef.current) === null || _tableRef$current13 === void 0 ? void 0 : _tableRef$current13.querySelectorAll('th')[index].removeAttribute('aria-sort');
      setDescription(true, sortKey, index, order1);
    } else if (sortType === 1 || sortType === 0 && skipInitialSort) {
      removeIconClass();
      animate(defaultIcon, 'rotateOut');
      sortupIcon.classList.add('sorted');
      defaultIcon.classList.add('sorted');
      hoverHead === null || hoverHead === void 0 ? void 0 : hoverHead.classList.add('head-sorted');
      setTimeout(function () {
        defaultIcon.classList.add('hidden');
        sortupIcon.classList.remove('hidden');
      }, 300);
      setDescription(true, sortKey, index, order2);
    } else if (sortType === 2) {
      removeIconClass();
      animate(sortupIcon, 'rotateOut');
      defaultIcon.classList.add('hidden');
      sortupIcon.classList.remove('hidden');
      sortupIcon === null || sortupIcon === void 0 ? void 0 : sortupIcon.classList.add('sorted');
      hoverHead === null || hoverHead === void 0 ? void 0 : hoverHead.classList.add('head-sorted');
      setTimeout(function () {
        sortupIcon === null || sortupIcon === void 0 ? void 0 : sortupIcon.classList.add('descendant');
      }, 300);
      setDescription(false, sortKey, index);
    }
    if (disableDefaultSort) {
      initStrips();
      setStrips();
    }
  };
  var sort = function sort(sortKey, sortType) {
    if (!sortKey) return;
    var index = 0,
      sortFunc1,
      sortFunc2;
    if (Array.isArray(children) && children.length > 0) {
      index = children.findIndex(function (item) {
        return item.props.sortKey === sortKey;
      });
      if (index !== -1) {
        var active = children.find(function (item) {
          return item.props.sortKey === sortKey;
        });
        sortFunc1 = active.props.sortFunc1;
        sortFunc2 = active.props.sortFunc2;
      } else {
        console.warn("sortKey '" + sortKey + "' is wrong.");
        return;
      }
    }
    if (disableDefaultSort) {
      if (sortType === 2 && !skipInitialSort) {
        changeSortIcon(sortKey, sortType, index, sortFunc1, sortFunc2, skipInitialSort);
      } else if (sortType === 0 || sortType === 2 && skipInitialSort) {
        // 1st sorting phase
        changeSortIcon(sortKey, sortType, index, sortFunc1, sortFunc2, skipInitialSort);
      } else if (sortType === 1) {
        changeSortIcon(sortKey, sortType, index, sortFunc1, sortFunc2, skipInitialSort);
      }
      return;
    }
    if (currentPage && originalData) initialData.current = [].concat(originalData);
    if (sortType === 0 && !skipInitialSort) {
      changeSortIcon(sortKey, sortType, index, sortFunc1, sortFunc2, skipInitialSort);
      initialData.current.sort(function (a, b) {
        return a._initialSortIndex > b._initialSortIndex ? 1 : -1;
      });
      // setSortedData(sortedData);
      initialData.current.forEach(function (data, i) {
        data._previousSortIndex = data._nextSortIndex;
        data._nextSortIndex = data._initialSortIndex;
      });
      sortAnimate();
    } else if (sortType === 1 || sortType === 0 && skipInitialSort) {
      // 1st sorting phase
      changeSortIcon(sortKey, sortType, index, sortFunc1, sortFunc2, skipInitialSort);
      var sortFunction;
      if (sortFunc1) {
        var _tableRef$current14;
        sortFunction = sortFunc1;
        (_tableRef$current14 = tableRef.current) === null || _tableRef$current14 === void 0 ? void 0 : _tableRef$current14.querySelectorAll('th')[index].removeAttribute('aria-sort');
      } else {
        var _tableRef$current15;
        sortFunction = function sortFunction(a, b) {
          return defaultSortFunc(a, b, sortKey, sortType);
        };
        (_tableRef$current15 = tableRef.current) === null || _tableRef$current15 === void 0 ? void 0 : _tableRef$current15.querySelectorAll('th')[index].setAttribute('aria-sort', 'ascending');
      }
      initialData.current.sort(sortFunction);
      // setSortedData(sortedData);
      initialData.current.forEach(function (data, i) {
        data._previousSortIndex = data._nextSortIndex;
        data._nextSortIndex = i;
      });
      sortAnimate();
    } else if (sortType === 2) {
      // 2nd sorting phase
      changeSortIcon(sortKey, sortType, index, sortFunc1, sortFunc2, skipInitialSort);
      var _sortFunction;
      if (sortFunc2) {
        var _tableRef$current16;
        _sortFunction = sortFunc2;
        (_tableRef$current16 = tableRef.current) === null || _tableRef$current16 === void 0 ? void 0 : _tableRef$current16.querySelectorAll('th')[index].removeAttribute('aria-sort');
      } else {
        var _tableRef$current17;
        _sortFunction = function _sortFunction(a, b) {
          return defaultSortFunc(a, b, sortKey, sortType);
        };
        (_tableRef$current17 = tableRef.current) === null || _tableRef$current17 === void 0 ? void 0 : _tableRef$current17.querySelectorAll('th')[index].setAttribute('aria-sort', 'descending');
      }
      initialData.current.sort(_sortFunction);
      // setSortedData(sortedData);
      initialData.current.forEach(function (data, i) {
        data._previousSortIndex = data._nextSortIndex;
        data._nextSortIndex = i;
      });
      sortAnimate();
    }
  };
  var renderTableHeader = function renderTableHeader() {
    var results = [];
    React.Children.forEach(children, function (child, index) {
      if (!child) {
        return;
      }
      var _child$props = child.props,
        sortKey = _child$props.sortKey,
        _child$props$sortId = _child$props.sortId,
        sortId = _child$props$sortId === void 0 ? String.fromCharCode(65 + Math.floor(Math.random() * 26)) + Date.now() : _child$props$sortId,
        _child$props$slot = _child$props.slot,
        slot = _child$props$slot === void 0 ? 'end' : _child$props$slot,
        children = _child$props.children,
        columnStyle = _child$props.style,
        columnClassName = _child$props.className;
      var classThNames = ClassNames("header-container", {
        "start-slot": slot === 'start',
        "end-slot": slot === 'end',
        "has-sorter": sortKey
      });
      var descId = 'table-sort-' + sortId;
      var classDefaultIconNames = ClassNames('Appkit4-icon icon-sorting-fill sort-table animated');
      var classSortupIconNames = ClassNames('Appkit4-icon icon-sorting-ascendant-fill sort-table animated hidden');

      // Add enter event for table sort
      var onkeyDown = function onkeyDown(event) {
        if (event.key === KEY_VALUES.ENTER) {
          event.preventDefault();
          if (event.target.className.indexOf('checkbox') < 0) handleSort(sortKey);
        }
      };
      var desc = sortKey ? {
        'aria-describedby': descId
      } : null;
      results.push( /*#__PURE__*/React.createElement("th", {
        key: index,
        style: columnStyle,
        className: columnClassName
      }, /*#__PURE__*/React.createElement("div", _extends({
        // tabIndex={sortKey ? 0 : -1}
        onClick: function onClick(event) {
          if (event.target.className.indexOf('checkbox') < 0) handleSort(sortKey);
        },
        onKeyDown: function onKeyDown(e) {
          return onkeyDown(e);
        },
        className: classThNames,
        role: "button",
        tabIndex: 0
      }, desc), /*#__PURE__*/React.createElement("span", null, children), sortKey &&
      /*#__PURE__*/
      //@ts-ignore
      React.createElement("div", {
        className: "sort-icon-wrapper",
        "aria-hidden": true
      }, /*#__PURE__*/React.createElement("span", {
        className: classDefaultIconNames
      }), /*#__PURE__*/React.createElement("span", {
        className: classSortupIconNames
      })), sortKey && /*#__PURE__*/React.createElement("span", {
        className: "ap-table-header-description-sr-only",
        id: descId
      }, "Click to sort rows by " + sortKey + " in ascending order"))));
    });
    return results;
  };
  var renderTableBody = function renderTableBody() {
    var results = [];
    sortedData.forEach(function (data, index) {
      if (!data) {
        return;
      }
      var onCheckboxChange = function onCheckboxChange(value) {
        sortedData.forEach(function (item) {
          if (item === data) {
            item.selected = value;
          }
        });
        setSortedData([].concat(sortedData));
        onCheckChange === null || onCheckChange === void 0 ? void 0 : onCheckChange(sortedData);
        setTimeout(function () {
          setStrips();
        });
      };
      var tdList = [];
      React.Children.forEach(children, function (child, index) {
        if (!child) {
          return;
        }
        var _child$props2 = child.props,
          sortKey = _child$props2.sortKey,
          field = _child$props2.field,
          renderCell = _child$props2.renderCell;
        if (field in data || sortKey in data) {
          tdList.push( /*#__PURE__*/React.createElement("td", {
            key: index
          }, renderCell ? renderCell(data, field || sortKey) : checkable && index === 0 ? /*#__PURE__*/React.createElement(Checkbox, {
            value: data[field] || data[sortKey],
            checked: data.selected,
            onChange: function onChange(value) {
              return onCheckboxChange(value);
            }
          }, /*#__PURE__*/React.createElement("span", null, data[field] || data[sortKey])) : /*#__PURE__*/React.createElement("span", null, field in data ? data[field] : sortKey in data ? data[sortKey] : '')));
        } else {
          tdList.push( /*#__PURE__*/React.createElement("td", {
            key: index
          }));
        }
      });
      results.push( /*#__PURE__*/React.createElement("tr", {
        key: index,
        className: (selectedIndexs === null || selectedIndexs === void 0 ? void 0 : selectedIndexs.indexOf(data[customSelectedItemKey])) > -1 || data.selected ? "selected" : ""
        // @ts-ignore
        ,
        onClick: function onClick(event) {
          return onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick(event, index, data);
        }
      }, tdList));
    });
    return results;
  };
  var classWrapperNames = ClassNames("ap-table", className, {
    "ap-table-condensed": condensed,
    "ap-table-striped": striped,
    "ap-table-checkbox": checkable
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classWrapperNames,
    style: style,
    id: id || idInfo,
    ref: mergeRefs(ref, tableRef)
  }, /*#__PURE__*/React.createElement("table", {
    className: "" + (tableHeadPadding ? 'table-head-padding' : '')
  }, hasTitle && /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, renderTableHeader())), /*#__PURE__*/React.createElement("tbody", null, renderTableBody())));
});
export default Table;